// Applications (MFA API apps) on the shared policy screens (see
// ../adapterContract.js; products/ad.jsx is the reference). Replaces
// pages/applications/AppPolicyForm and the AppPolicies list.
//
// One app policy = one MFA API app, one directory user (AD or Entra, by account
// name, with their directory's id) and an action. With Require MFA the push
// goes to one console (IAM) user, "Approver for the push" (in Where), stored
// in permissions.iam_users. No Everyone, no groups or OUs and no Except (the
// policy names one user), no fallback (a user no app policy covers is already
// refused), no approval or session. Policy service type "app"
// (service/appPolicyService).
//
// Create answers 409 when the same user already has a policy for the app: the
// shell names the duplicate and offers "Create anyway" (force: true). Editing
// makes a new version with a new id, which update() returns.
//
// /pam/policies/apps/new?app=<id> starts with that app picked.
import React, { useEffect, useMemo, useState } from "react";
import { Select, Spin } from "antd";
import { useParams, useSearchParams } from "react-router-dom";

import { AN_COLORS, AN_MONO } from "../../../design-system/tokens";
import {
  appPolicyFields,
  createAppPolicy,
  deleteAppPolicy,
  directoryLabel,
  directoryUserMatches,
  listAppPolicies,
  listDirectoryUsers,
  listIamUsers,
  updateAppPolicy,
} from "../../../service/appPolicyService";
import { policyAction } from "../../../service/adPolicyService";
import { DOMAIN_ID, ORG_ID } from "../../../constants";
import { callService, defineAdapter, policyFailure } from "../adapterContract";
import { Field, HintText } from "../ui";
import RemotePicker from "../editor/RemotePicker";
import { fieldDomId } from "../editor/draft";
import { appText, directoryText, loadApps, loadDirectoryInfo, userKey, userMissing } from "./apps/appsData";

const SEARCH_DELAY_MS = 300;
const EMPTY = new Set();

// appPolicyService rejects with { status, text, logout, invalid }: invalid is
// the reason of a 400 "Invalid policy: <reason>". An update of a policy that
// was deleted or replaced answers code 404 / 500 inside a 200.
const GONE = /only policies with 'active' status|does not exist|not found/i;
const fail = (f) => {
  const p = policyFailure(f);
  if (f?.invalid) return { ...p, kind: "invalid", reason: f.invalid };
  if ([404, 500].includes(Number(f?.status)) && GONE.test(String(f?.text || ""))) return { ...p, kind: "gone" };
  return p;
};

const flowOf = (pj) => {
  const f = String(pj?.policyFlow || "mfa_required").toLowerCase();
  if (f === "allow") return "allow";
  if (f === "block" || f === "deny") return "block";
  return "mfa_required";
};

/* ------------------------------------------------------------------- data */

function useAppsData(draft, setProduct) {
  const editing = !!draft;
  const { policyId } = useParams();
  const [params] = useSearchParams();
  const [apps, setApps] = useState(null);
  const [directory, setDirectory] = useState(null);
  const [taken, setTaken] = useState(EMPTY);

  useEffect(() => {
    let live = true;
    loadApps()
      .then((list) => live && setApps(list || []))
      .catch(() => live && setApps([]));
    loadDirectoryInfo().then((info) => live && setDirectory(info));
    return () => {
      live = false;
    };
  }, []);

  // ?app=<id> picks the app of a new policy (the app's Policies tab links here).
  const appId = draft?.product?.appId || "";
  const preset = params.get("app") || "";
  useEffect(() => {
    if (editing && !appId && preset && !policyId) setProduct({ appId: preset }, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // The app's other policies: their users are shown as "has a policy".
  useEffect(() => {
    if (!editing || !appId) {
      setTaken(EMPTY);
      return undefined;
    }
    let live = true;
    listAppPolicies({ appId })
      .then((list) => {
        if (!live) return;
        const set = new Set();
        list
          .filter((p) => String(p.id) !== String(policyId || ""))
          .map(appPolicyFields)
          .forEach((f) => f.user && set.add(userKey(f.directoryId, f.user)));
        setTaken(set);
      })
      .catch(() => live && setTaken(EMPTY));
    return () => {
      live = false;
    };
  }, [editing, appId, policyId]);

  return { apps, directory, taken, setProduct };
}

// A policy for "any directory" (0) covers the account in every directory.
const hasPolicy = (taken, u) => taken.has(userKey(u.directoryId, u.account)) || taken.has(userKey(0, u.account));

/* -------------------------------------------------------------- sections */

const twoLine = (top, bottom) => (
  <div style={{ display: "grid", minWidth: 0, lineHeight: 1.35, padding: "0.125rem 0" }}>
    <span style={{ fontFamily: AN_MONO, fontSize: "0.8125rem", overflow: "hidden", textOverflow: "ellipsis" }}>{top}</span>
    {bottom && <span style={{ fontSize: "0.75rem", color: AN_COLORS.muted, overflow: "hidden", textOverflow: "ellipsis" }}>{bottom}</span>}
  </div>
);

// Who: one directory user. Searched on username, UPN, display name and email
// in the loaded users; when the directory has more than were loaded, the
// server is asked too (it matches the email only).
function DirectoryUserPicker({ value, onChange, draft, data, disabled }) {
  const account = (value || [])[0] || "";
  const directoryId = Number(draft.product.directoryId) || 0;
  const info = data?.directory || null;
  const taken = data?.taken || EMPTY;
  const [query, setQuery] = useState("");
  const [remote, setRemote] = useState({ users: [], loading: false });

  const q = query.trim();
  const askServer = !!info && !info.complete && !!q;
  useEffect(() => {
    if (!askServer) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      setRemote((r) => ({ ...r, loading: true }));
      listDirectoryUsers({ search: q, pageSize: 50 })
        .then((res) => live && setRemote({ users: res.users || [], loading: false }))
        .catch(() => live && setRemote({ users: [], loading: false }));
    }, SEARCH_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [askServer, q]);

  const selected = account ? userKey(directoryId, account) : undefined;
  const options = useMemo(() => {
    const byKey = new Map();
    (info?.users || []).filter((u) => directoryUserMatches(u, q)).forEach((u) => byKey.set(userKey(u.directoryId, u.account), u));
    if (askServer) remote.users.forEach((u) => !byKey.has(userKey(u.directoryId, u.account)) && byKey.set(userKey(u.directoryId, u.account), u));
    if (selected && !byKey.has(selected)) byKey.set(selected, { account, directoryId, email: "" });
    return [...byKey.entries()].map(([key, u]) => {
      const where = directoryLabel(info?.dirs, u.directoryId, { entra: u.entra });
      const busy = key !== selected && hasPolicy(taken, u);
      return {
        value: key,
        disabled: busy,
        label: twoLine(u.account, [u.email, where, busy && "has a policy"].filter(Boolean).join(" · ")),
        display: `${u.account} · ${where}`,
        user: u,
      };
    });
  }, [info, q, askServer, remote.users, selected, account, directoryId, taken]);

  const searching = !info || (askServer && remote.loading);
  return (
    <>
      <Select
        id="pf-who-users"
        aria-label="Directory user"
        showSearch
        allowClear
        filterOption={false}
        placeholder="Search by name, username or email"
        value={selected}
        searchValue={query}
        onSearch={setQuery}
        onChange={(v, opt) => {
          const u = opt?.user || null;
          onChange(u ? u.account : "");
          if (data?.setProduct) data.setProduct({ directoryId: u ? Number(u.directoryId) || 0 : 0 });
          setQuery("");
        }}
        onOpenChange={(open) => !open && setQuery("")}
        options={options}
        optionLabelProp="display"
        notFoundContent={searching ? <Spin size="small" /> : "No directory users found"}
        disabled={disabled}
        listHeight={320}
        style={{ width: "100%" }}
      />
      {account && (
        <HintText>
          Sent as <span style={{ fontFamily: AN_MONO }}>{account}</span> in {directoryLabel(info?.dirs, directoryId)}. The app must send this user's
          username, email or DOMAIN\user.
        </HintText>
      )}
    </>
  );
}

function Where(props) {
  const { product, setProduct, errors, touch, data, draft } = props;
  const apps = data?.apps;
  const mfaApps = (apps || []).filter((a) => a.protocol === "mfa_api");
  const options = mfaApps.map((a) => ({ value: a.id, label: a.name || a.id }));
  if (product.appId && !mfaApps.some((a) => a.id === product.appId)) {
    options.unshift({ value: product.appId, label: apps ? `${appText(apps, product.appId)}, not an MFA API app` : product.appId });
  }
  let placeholder = "Choose an MFA API app";
  if (apps === null) placeholder = "Loading applications…";
  else if (!mfaApps.length) placeholder = "No MFA API apps yet";
  return (
    <>
      <Field label="Application" required error={errors["where.appId"]} htmlFor={fieldDomId("where.appId")} help="Only MFA API apps take policies.">
        <Select
          id={fieldDomId("where.appId")}
          aria-label="Application"
          showSearch
          optionFilterProp="label"
          placeholder={placeholder}
          value={product.appId || undefined}
          onChange={(v) => setProduct({ appId: v || "" })}
          onBlur={() => touch("where.appId")}
          options={options}
          loading={apps === null}
          status={errors["where.appId"] ? "error" : undefined}
          notFoundContent="No MFA API apps yet"
          style={{ width: "100%" }}
        />
      </Field>
      <Approver product={product} setProduct={setProduct} errors={errors} touch={touch} action={draft.action} />
    </>
  );
}

const searchIamUsers = (q) =>
  listIamUsers({ search: q })
    .then((list) => list.map((u) => ({ value: u.email, label: u.email, sub: u.name || "" })))
    .catch(() => {
      throw new Error("Couldn't load console users.");
    });

const APPROVER_LABEL = "Approver for the push";

function Approver({ product, setProduct, errors, touch, action }) {
  if (action !== "mfa_required") return null;
  return (
    <Field
      label={APPROVER_LABEL}
      required
      error={errors["where.approver"]}
      htmlFor={fieldDomId("where.approver")}
      help="One console user. Their phone gets the push when this user signs in to the app."
    >
      <RemotePicker
        id={fieldDomId("where.approver")}
        ariaLabel={APPROVER_LABEL}
        single
        sources={[{ key: "iam", label: "Console users", search: searchIamUsers }]}
        value={product.approver || ""}
        onChange={(v) => setProduct({ approver: v || "" })}
        onBlur={() => touch("where.approver")}
        placeholder="Search console users"
        status={errors["where.approver"] ? "error" : undefined}
      />
    </Field>
  );
}

/* --------------------------------------------------------------- adapter */

const appsAdapter = defineAdapter({
  key: "apps",
  policyType: "app",
  modeKey: "mfa_apps",
  labels: {
    product: "app",
    eyebrow: "Applications · Policies",
    listDescription: "Who may use each MFA API app, and who approves the push.",
    emptySentence: "Without a policy, users are refused.",
    subject: "sign-in",
  },

  // Each row carries appsView: the app's name and the "Not in the directory"
  // check, for the list's Who and Where.
  list: async () => {
    const [rows, apps, dir] = await Promise.all([
      listAppPolicies().catch((f) => {
        throw fail(f);
      }),
      loadApps().catch(() => null),
      loadDirectoryInfo(),
    ]);
    return rows.map((r) => {
      const f = appPolicyFields(r);
      return {
        ...r,
        appsView: { appName: appText(apps, f.appId), userMissing: userMissing(dir?.known, f.user, f.directoryId) },
      };
    });
  },
  create: ({ policyJson, force }) =>
    createAppPolicy({ policyJson, force }).then(
      (res) => {
        if (res?.conflict) throw policyFailure({ conflicts: res.conflict.conflicts, warning: res.conflict.warning }, "conflict");
        return { id: res?.id || null, warning: res?.warning || "" };
      },
      (f) => {
        throw fail(f);
      },
    ),
  update: ({ id, policyJson }) =>
    updateAppPolicy({ id, policyJson }).then(
      (res) => ({ id: res?.id || id, warning: "" }),
      (f) => {
        throw fail(f);
      },
    ),
  setActive: (row, active) =>
    callService(policyAction, {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      action: { actionType: "status", actionIds: [row.id], actionValue: active ? "Approved" : "Suspended" },
    }),
  // Always the id from the list: an older id deletes only an old version.
  remove: (row) =>
    deleteAppPolicy(row.id).catch((f) => {
      throw fail(f);
    }),

  emptyDraft: () => ({
    who: { everyone: false, users: [] },
    product: { appId: "", directoryId: 0, approver: "" },
  }),
  fromPolicy: (row) => {
    const f = appPolicyFields(row);
    return {
      who: { everyone: false, users: f.user ? [f.user] : [] },
      product: { appId: f.appId, directoryId: f.directoryId, approver: f.iamUser },
    };
  },
  actionOf: flowOf,
  // Starts from what was stored; overwrites the app, the user and (when one is
  // picked) the approver. Conditions and policyFlow are the shell's.
  toPolicyJson: (draft, stored) => {
    const s = stored || {};
    const p = draft.product;
    const out = {
      ...s,
      policyName: draft.policyName.trim(),
      policyType: "app",
      app: {
        ...(s.app || {}),
        apps: p.appId ? [p.appId] : [],
        users: (draft.who.users || []).slice(0, 1),
        directoryId: Number(p.directoryId) || 0,
      },
    };
    if (p.approver) out.permissions = { ...(s.permissions || {}), iam_users: [p.approver] };
    return out;
  },
  validate: (draft) => {
    const e = {};
    if (!draft.product.appId) e["where.appId"] = "Pick an application.";
    if (draft.action === "mfa_required" && !draft.product.approver) e["where.approver"] = "Pick who approves the push.";
    return e;
  },

  useData: useAppsData,
  Where,
  who: {
    everyone: false,
    except: false,
    fields: [{ key: "users", kind: "users", label: "Directory user", single: true, Picker: DirectoryUserPicker }],
  },

  listWho: (row) => {
    const user = appPolicyFields(row).user;
    return { everyone: false, names: user ? [row.appsView?.userMissing ? `${user} (not in the directory)` : user] : [] };
  },
  listWhere: (row) => row.appsView?.appName || appText(null, appPolicyFields(row).appId),
  sentenceWhere: (draft, data) => {
    const p = draft.product;
    if (!p.appId) return "";
    const app = `in ${appText(data?.apps || null, p.appId)}`;
    return draft.action === "mfa_required" && p.approver ? `${app}, approved by ${p.approver}` : app;
  },
  reviewRows: (draft, data) => {
    const p = draft.product;
    const user = (draft.who.users || [])[0];
    const missing = userMissing(data?.directory?.known, user, p.directoryId);
    const rows = [
      { key: "app", label: "Application", value: appText(data?.apps || null, p.appId), section: "where" },
      {
        key: "directory",
        label: "Directory",
        value: `${directoryText(data?.directory?.dirs, p.directoryId)}${missing ? ". Not in the directory: this policy matches nobody." : ""}`,
        section: "where",
      },
    ];
    if (draft.action === "mfa_required") rows.push({ key: "approver", label: APPROVER_LABEL, value: p.approver || "Nobody yet", section: "where" });
    return rows;
  },

  features: {
    fallback: false,
    approval: false,
    session: false,
    actionSentences: { mfa_required: "Require MFA: a push goes to the approver picked above before the sign-in goes through." },
  },
});

export default appsAdapter;
